import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'src/hooks/useAuth';
import { useNavigate } from 'react-router-dom';

import { Button, Grid, IconButton, Modal, Paper, TextField, Typography, Snackbar, Alert } from '@mui/material'
import { isActionPermitted, permissions } from 'src/utils/helpers'
import { Close, Email } from '@mui/icons-material';

interface PurchaseCreditsProps {
  billingPlan?: {
    amount?: number;
    credits?: number;
    name?: string;
    [key: string]: any;
  };
  billingType?: string;
}

const PurchaseCredits: React.FC<PurchaseCreditsProps> = ({ billingPlan, billingType }) => {
  const { ready, t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [purchaseCreditsOpen, setPurchaseCreditsOpen] = useState(false);
  const [salesContactOpen, setSalesContactOpen] = useState(false);
  const [amount, setAmount] = useState<number | ''>('');
  const [amountError, setAmountError] = useState('');
  const [salesForm, setSalesForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    requestedCredits: ''
  });
  const [salesFormErrors, setSalesFormErrors] = useState<{ [key: string]: string }>({});
  const [salesFormSubmitted, setSalesFormSubmitted] = useState(false);

  const validateAmount = (value: number | '') => {
    if (value === '' || value === 0) {
      setAmountError(t('BILLING_PLAN_AMOUNT_REQUIRED'));
      return false;
    }
    if (value < 0) {
      setAmountError(t('BILLING_PLAN_AMOUNT_MUST_BE_POSITIVE'));
      return false;
    }
    setAmountError('');
    return true;
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? '' : Number(e.target.value);
    setAmount(value);
    validateAmount(value);
  };

  const isValidAmount = amount !== '' && amount > 0;
  const unitPriceInCents = billingPlan?.amount || 0;
  const unitPriceInEuros = unitPriceInCents / 100;
  const totalAmountInCents = typeof amount === 'number' ? amount * unitPriceInCents : 0;
  const totalAmountInEuros = totalAmountInCents / 100;

  const formatEuroAmount = (amount: number): string => {
    return amount.toFixed(2).replace('.', ',');
  };

  const validateSalesForm = () => {
    const errors: { [key: string]: string } = {};

    if (!salesForm.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!salesForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(salesForm.email)) {
      errors.email = 'Email is invalid';
    }

    if (!salesForm.company.trim()) {
      errors.company = 'Company is required';
    }

    if (!salesForm.requestedCredits.trim()) {
      errors.requestedCredits = 'Requested credits amount is required';
    }

    setSalesFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSalesFormChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setSalesForm(prev => ({
      ...prev,
      [field]: e.target.value
    }));

    // Clear error when user starts typing
    if (salesFormErrors[field]) {
      setSalesFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSalesFormSubmit = () => {
    if (validateSalesForm()) {
      // TODO: SEND EMAIL

      setSalesFormSubmitted(true);
      setSalesContactOpen(false);

      setSalesForm({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: '',
        requestedCredits: ''
      });
    }
  };

  const openSalesContact = () => {
    setSalesForm(prev => ({
      ...prev,
      requestedCredits: amount.toString()
    }));
    setSalesContactOpen(true);
    setPurchaseCreditsOpen(false);
  };

  return (
    <>
      <Button
        variant="contained"
        sx={styles.changeBtn}
        onClick={() => setPurchaseCreditsOpen(true)}
        disabled={!isActionPermitted(permissions.subscriptionsUpgrade, user?.permissions)}
      >
        {ready && t('PURCHASE_CREDITS_BTN')}
      </Button>

      <Modal
        open={purchaseCreditsOpen}
        sx={styles.modal}
        onClose={() => setPurchaseCreditsOpen(false)}
      >
        <Paper style={styles.paper}>
          <Grid
            container
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
          >
            <IconButton
              style={styles.closeBtn}
              onClick={() => setPurchaseCreditsOpen(false)}
            >
              <Close fontSize="small" />
            </IconButton>
          </Grid>

          <Grid
            style={styles.modalBody}
          >
            <Typography
              variant="h2"
              sx={styles.cancelSubsHeader}
            >
              {t('BILLING_PLAN_PURCHASE_CREDITS')}
            </Typography>

            <TextField
              style={styles.fieldStyle}
              fullWidth
              label={ready && t('MY_PLAN_PURCHASE_CREDITS_AMOUNT')}
              name="amount"
              onChange={handleAmountChange}
              value={amount}
              type="number"
              variant="outlined"
              error={!!amountError}
              helperText={amountError}
              inputProps={{
                min: 1,
                step: 1
              }}
            />

            {billingPlan && amount && typeof amount === 'number' && amount > 0 && (
              <Grid>
                <Typography >
                  Total Amount: €{formatEuroAmount(totalAmountInEuros)}
                </Typography>
              </Grid>
            )}

            <Grid style={styles.buttonContainer}>
              <Button
                sx={styles.cancelSubs}
                variant="contained"
                onClick={() => navigate(`/subscription/checkout-new?type=2&credits=${amount}&totalAmount=
                  ${totalAmountInEuros}&planAmount=${unitPriceInEuros}
                  &billingPlanId=${billingPlan?.id || billingPlan?.planId || ''}
                  &isEnterprise=${billingType === 'enterprise'}`)}
                disabled={
                  !isActionPermitted(permissions.subscriptionsBuyExtra, user?.permissions) ||
                  !isValidAmount ||
                  (typeof amount === 'number' && amount > 2500)
                }
              >
                {t('PURCHASE_CREDITS_PAYMENT_BUTTON')}
              </Button>

              {typeof amount === 'number' && amount > 2500 && (
                <Button
                  sx={styles.contactSalesBtn}
                  variant="outlined"
                  startIcon={<Email />}
                  onClick={openSalesContact}
                  disabled={!isActionPermitted(permissions.subscriptionsBuyExtra, user?.permissions)}
                >
                  Contact Sales
                </Button>
              )}
            </Grid>
          </Grid>
        </Paper>
      </Modal>

      <Modal
        open={salesContactOpen}
        sx={styles.modal}
        onClose={() => setSalesContactOpen(false)}
      >
        <Paper style={styles.salesPaper}>
          <Grid
            container
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
          >
            <IconButton
              style={styles.closeBtn}
              onClick={() => setSalesContactOpen(false)}
            >
              <Close fontSize="small" />
            </IconButton>
          </Grid>

          <Grid style={styles.modalBody}>
            <Typography
              variant="h2"
              sx={styles.cancelSubsHeader}
            >
              Contact Sales Team
            </Typography>

            <Typography
              variant="body1"
              sx={styles.salesDescription}
            >
              For purchases over 2,500 credits, our sales team will provide you with custom pricing and support.
            </Typography>

            <TextField
              style={styles.fieldStyle}
              fullWidth
              label="Full Name *"
              value={salesForm.name}
              onChange={handleSalesFormChange('name')}
              variant="outlined"
              error={!!salesFormErrors.name}
              helperText={salesFormErrors.name}
            />

            <TextField
              style={styles.fieldStyle}
              fullWidth
              label="Email Address *"
              type="email"
              value={salesForm.email}
              onChange={handleSalesFormChange('email')}
              variant="outlined"
              error={!!salesFormErrors.email}
              helperText={salesFormErrors.email}
            />

            <TextField
              style={styles.fieldStyle}
              fullWidth
              label="Company Name *"
              value={salesForm.company}
              onChange={handleSalesFormChange('company')}
              variant="outlined"
              error={!!salesFormErrors.company}
              helperText={salesFormErrors.company}
            />

            <TextField
              style={styles.fieldStyle}
              fullWidth
              label="Phone Number"
              value={salesForm.phone}
              onChange={handleSalesFormChange('phone')}
              variant="outlined"
            />

            <TextField
              style={styles.fieldStyle}
              fullWidth
              label="Requested Credits *"
              type="number"
              value={salesForm.requestedCredits}
              onChange={handleSalesFormChange('requestedCredits')}
              variant="outlined"
              error={!!salesFormErrors.requestedCredits}
              helperText={salesFormErrors.requestedCredits}
              inputProps={{ min: 1, step: 1 }}
            />

            <TextField
              style={styles.fieldStyle}
              fullWidth
              label="Additional Message"
              multiline
              rows={4}
              value={salesForm.message}
              onChange={handleSalesFormChange('message')}
              variant="outlined"
              placeholder="Tell us about your specific requirements, timeline, or any questions you have..."
            />

            <Grid style={styles.buttonContainer}>
              <Button
                sx={styles.cancelSubs}
                variant="contained"
                onClick={handleSalesFormSubmit}
                startIcon={<Email />}
              >
                Send Request to Sales
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Modal>

      <Snackbar
        open={salesFormSubmitted}
        autoHideDuration={6000}
        onClose={() => setSalesFormSubmitted(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSalesFormSubmitted(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          Your request has been sent to our sales team. We'll contact you soon!
        </Alert>
      </Snackbar>
    </>
  )
}

export default PurchaseCredits;

const styles = {
  changeBtn: {
    textTransform: 'none',
    backgroundColor: '#3E8AFF',
    color: '#FFFFFF',
  },
  contactSalesBtn: {
    textTransform: 'none',
    marginLeft: '12px',
    color: '#3E8AFF',
    borderColor: '#3E8AFF',
    '&:hover': {
      borderColor: '#2b6ee6',
      backgroundColor: 'rgba(62, 138, 255, 0.04)',
    },
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0px 16px',
    maxHeight: 760,
  },
  paper: {
    // maxHeight: '94%',
    // overflow: 'scroll',
  },
  salesPaper: {
    maxHeight: '90vh',
    overflow: 'auto',
  },
  closeBtn: {
    padding: '2!important',
  },
  modalBody: {
    maxWidth: 600,
    padding: '0 24px 24px 24px',
    maxHeight: 760,
    width: 500
  },
  cancelSubsHeader: {
    color: '#4C4E64',
    textAlign: 'center',
    fontSize: 30,
    fontWeight: '500',
    marginBottom: '24px'
  },
  salesDescription: {
    color: '#666',
    textAlign: 'center',
    fontSize: 14,
    marginBottom: '24px',
    lineHeight: 1.5
  },
  fieldStyle: {
    paddingBottom: 24
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  cancelSubs: {
    fontWeight: '600',
    textTransform: 'capitalize',
    backgroundColor: '#3E8AFF'
  },
  noPadding: {
    padding: '0px 16px',
    color: '#4C4E64',
    fontSize: '13px'
  },
  planInfoContainer: {
    marginBottom: '16px',
    padding: '12px',
    backgroundColor: '#f5f5f5',
    borderRadius: '8px'
  },
  planInfo: {
    color: '#666',
    fontSize: '14px',
    marginBottom: '4px'
  }
}
